"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useControlledQuantityPicker = void 0;
var _logger = require("@ori/logger");
var _react = require("react");
var _contexts = require("../../contexts");
var _logger2 = require("../../logger");
var _utils = require("../../utils");
const useControlledQuantityPicker = ({
  canBeDeleted,
  canBeReduced,
  endConsumerOrderId,
  initialQuantity,
  itemCode,
  lineNumber,
  maxQuantity,
  quantity,
  placement,
  removeItemHandler
}) => {
  const {
    setIsLoadingAdditionalData
  } = (0, _contexts.useBasketContext)();
  const logger = (0, _logger.useLogger)('useControlledQuantityPicker');
  const minQuantity = (0, _react.useMemo)(() => canBeDeleted ? canBeReduced ? 0 : initialQuantity : 1, [canBeDeleted, canBeReduced, initialQuantity]);

  /**
   * This handler is called when the quantity is changed for items in consumer order.
   */
  const updateCustomerOrderItemHandler = (0, _react.useCallback)(async updatedQuantity => {
    (0, _logger2.logDebug)(`updateCustomerOrderItemHandler triggered with value ${updatedQuantity}.`);
    if (typeof endConsumerOrderId === 'number' && itemCode) {
      try {
        setIsLoadingAdditionalData(true);
        await (0, _utils.updateCustomerOrderItem)({
          endConsumerOrderId,
          itemCode,
          requestedQuantity: updatedQuantity
        });
      } catch (error) {
        (0, _utils.showErrorNotification)();
        if (error instanceof Error) {
          logger.error(error);
        } else {
          const newError = new Error('updateCustomerOrderItemHandler Error', {
            cause: error
          });
          logger.error(newError);
        }
      } finally {
        setIsLoadingAdditionalData(false);
      }
    }
  }, [endConsumerOrderId, itemCode, logger, setIsLoadingAdditionalData]);

  /**
   * This handler is called when the quantity is changed for all item except items in consumer order.
   */
  const updateQuantityHandler = (0, _react.useCallback)(async ({
    itemCode: code,
    lineNumber: line,
    quantity: currentQuantity,
    previousQuantity
  }) => {
    (0, _logger2.logDebug)(`updateQuantityHandler triggered for ${code} in line ${line} with quantity ${currentQuantity} and previous quantity was ${previousQuantity}.`);
    try {
      setIsLoadingAdditionalData(true);
      await (0, _utils.updateBasketItem)({
        itemCode: code,
        lineNumber: line,
        placement,
        previousQuantity,
        quantity: currentQuantity
      });
    } catch (error) {
      (0, _utils.showErrorNotification)();
      if (error instanceof Error) {
        logger.error(error);
      } else {
        const newError = new Error('updateQuantityHandler Error', {
          cause: error
        });
        logger.error(newError);
      }
    } finally {
      setIsLoadingAdditionalData(false);
    }
  }, [logger, placement, setIsLoadingAdditionalData]);

  /**
   * This handler is called when the quantity is changed.
   */
  const quantityChangeHandler = (0, _react.useCallback)(async inputValue => {
    (0, _logger2.logDebug)(`quantityChangeHandler triggered for ${itemCode} in line ${lineNumber} with input value ${inputValue} and quantity is ${quantity}.`);
    // we do all checks one more time even this is done in the component
    // do not trigger anything if the input value is not a number and equal or greater than 0
    if (typeof inputValue !== 'number' || typeof inputValue === 'number' && inputValue < 0) {
      return;
    }
    let currentValue = inputValue;
    // remove item if the quantity is 0
    if (currentValue === 0) {
      void removeItemHandler();
      return;
    }
    // check min value
    if (currentValue < minQuantity) {
      currentValue = minQuantity;
      (0, _logger2.logDebug)(`Current value was limited to min quantity ${minQuantity}.`);
    }
    // check max value
    if (currentValue > maxQuantity) {
      currentValue = maxQuantity;
      (0, _logger2.logDebug)(`Current value was limited to min quantity ${minQuantity}.`);
    }
    // there was no change in quantity
    if (currentValue === quantity) {
      return;
    }
    if (typeof endConsumerOrderId === 'number') {
      await updateCustomerOrderItemHandler(currentValue);
    } else {
      await updateQuantityHandler({
        itemCode,
        lineNumber,
        quantity: currentValue,
        previousQuantity: initialQuantity
      });
    }
  }, [endConsumerOrderId, initialQuantity, itemCode, lineNumber, maxQuantity, minQuantity, quantity, removeItemHandler, updateCustomerOrderItemHandler, updateQuantityHandler]);

  /**
   * This is the callback that is passed to the QuantityPicker component.
   */
  const onQuantityChange = (0, _react.useCallback)((inputValue, id, source) => {
    (0, _logger2.logDebug)(`QuantityPicker.changeQuantity() -> inputValue: ${inputValue}, id: ${id}, source: ${source}`);
    quantityChangeHandler(inputValue).catch(error => {
      if (error instanceof Error) {
        logger.error(error);
      } else {
        const newError = new Error('onQuantityChange Error', {
          cause: error
        });
        logger.error(newError);
      }
      (0, _utils.showErrorNotification)();
    });
  }, [quantityChangeHandler, logger]);
  return {
    onQuantityChange,
    quantityChangeHandler,
    updateCustomerOrderItemHandler,
    updateQuantityHandler
  };
};
exports.useControlledQuantityPicker = useControlledQuantityPicker;